// ==============================================================================================
//  APPLICATION AUTHORSHIP & RIGHTS NOTICE
//
//  Developed by: Gareth Morgan
//  Brand & Product: BuildMyChecks.co.uk
//  Ownership: Sole author and rights holder
//
//  ----------------------------------------------------------------------------------------------
//  NOTICE:
//  - This application and its related systems were designed, developed, and built solely
//    by Gareth Morgan.
//  - Gareth Morgan retains full authorship, intellectual property, and creative rights to
//    all Build My Checks branding, technology, source code, architecture, and design.
//  - No ownership, license, or implied rights are granted to any third party unless
//    explicitly agreed in writing by Gareth Morgan.
//  - Gareth Morgan is NOT responsible for future maintenance, updates, security patching,
//    or operational upkeep if this application is deployed, copied, or modified without
//    his written consent.
//  - Gareth Morgan reserves the right to:
//        Withdraw or disable access to the application or related services
//        Remove hosted, cloud-based, or linked services under his control
//        Revoke access to Build My Checksrelated assets, code, APIs, or integrations
//
//   2025 Gareth Morgan  BuildMyChecks.co.uk
//  Unauthorized use, alteration, copying, or redistribution without written consent
//  is strictly prohibited.
//
//  ----------------------------------------------------------------------------------------------
//  Build My Checks  ASCII Logo Signature
//  (Symbolic representation of BuildMyChecks.co.uk identity)
//
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................:-==++-.........:-:.............................................
// ...................................-*******:.......=*****+-.........................................
// ...................................=*******+:.....+*******=.........................................
// .........................::........+**********************=.........................................
// .......................-+***+-.:-++***********************=:.......:................................
// .....................-+*************************************+-..=+**+-..............................
// .....................:+*************+=-.............-++***************+:............................
// ......................:+********+=:.....................:=*************=............................
// .......................=******+:...........................-+********+-.............................
// ......................-*****+:...............................-+*****+-..............................
// ................:::::+****+-...................................=*****+..............................
// ...............+-......:=+-........+=.*%=......##:.:::.....:::..-*****=.............................
// ..............=*-*@%%%%@*.........-#*:#@=......@%::@@@*...*@@#...-+++++**++=-.......................
// .............:**-*@*..=@%.=#+..*#=-%#:#@=.=#%#+@%::@%%%-.=@%@#-##:..##:+*****:......................
// ..............-+-*@@%%@%+.+@*..#@==%#:#@==@%-:+@%::@#-@#:%#+%#.+@#.*@+-******=......................
// .................*@*..:#@++@*..#@==%#:#@=+@#...@%::@#.+@%%-=%#..%@#%#:+******+......................
// .................*@%##%@#.-%@%%@@==%#:#@=:#@%#%%%::@#.:#@*.=%#...%@%:=****+-:.......................
// ..................::........::.........:....::.....:....:......:-#@--***+:..........................
// ................:++:+@@@@@*.:@#:.....................#%=......:#%#-.+***+:..........................
// .............-+**+:#@*:.:+%+:@#-=+-...:=+=-....-+=-..#%=.:=-..-++-..+***+:..........................
// .............+***=-@%.......:@@#+#@#.+@#-+%#:+@%++@%:#%+*@#::%#--*-:*****+-.........................
// .............-***=:@%.......:@%:.-%%:%@%%%%%+#@=.....#@@@%:..#%@@%-=**+++++*+.......................
// ..............****-+%@*=+%@*:@%:.-%%.*@*-=*=.*@%==%#:#@+=@%=:++-+@*-+--===:+=.......................
// .................::::=***=:..++..:++..-+**=...:+**+:.=+-.:++:-*#*=::-:+***=::-++:...................
// ....................-****+=............................*#......+*****************=..................
// .....................=*****=.......+##+.:+##=....*+.-#-*#.**:..:+************++*=...................
// .....................+******+-....*%..:.%*.:%+..:%*.=%=*%%*..:--***********+:..-+:..................
// ....................+*********=:%=+%*+%-*%+*%-=#-#%*%@=*#:%*:+****+++****+=...=*****-...............
// ...................=**********+--::.::....::...:..::.:.:-::-:=***+:..=+*+:..:+******-...............
// ....................-******++*******+=-:............-=++****+=+***+:...:...=******+-:...............
// ......................-++-....=+***************************+=+-=****+:...-+******-..................
// ................................=**********************++-:....=******=:+********=..................
// ................................=**********************=......:+*****************+:.................
// ................................+*******=......+*******=........-+=.:=+****+-.-+-...................
// ................................:-=+***-........+******=..............=***+.........................
// .................................................--::...............................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
//
// ==============================================================================================

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, db, storage } from '../../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function SubscriptionManagement() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [company, setCompany] = useState(null);
  const [userCount, setUserCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    const loadCompany = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        const companyId = userData?.companyId;

        if (!companyId) {
          navigate('/setup');
          return;
        }

        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        const companyData = { id: companyId, ...companyDoc.data() };
        setCompany(companyData);
        setUserCount(companyData.userLimit || 1);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load company:', err);
        setLoading(false);
      }
    };

    loadCompany();
  }, [navigate]);

  /* ----------------------- Company Logo Upload ----------------------- */
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert(t('subscription.management.logoSizeError', 'Logo must be less than 2 MB'));
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert(t('subscription.management.logoTypeError', 'Please select an image file'));
      return;
    }
    setUploadingLogo(true);
    try {
      const logoRef = ref(storage, `companies/${company.id}/logo`);
      await uploadBytes(logoRef, file);
      const url = await getDownloadURL(logoRef);
      await updateDoc(doc(db, 'companies', company.id), { logoURL: url });
      setCompany((prev) => ({ ...prev, logoURL: url }));
      setLogoPreview(null);
      alert(t('subscription.management.logoUpdated', '✅ Company logo updated'));
    } catch (err) {
      console.error('Logo upload failed:', err);
      alert(t('subscription.management.logoUploadError', 'Failed to upload logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!window.confirm(t('subscription.management.logoRemoveConfirm', 'Remove the company logo? Reports will use the default BuildMyChecks logo.'))) return;
    setUploadingLogo(true);
    try {
      try { await deleteObject(ref(storage, `companies/${company.id}/logo`)); } catch {}
      await updateDoc(doc(db, 'companies', company.id), { logoURL: null });
      setCompany((prev) => ({ ...prev, logoURL: null }));
    } catch (err) {
      console.error('Logo removal failed:', err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSetupPayment = () => {
    // Redirect to checkout with current user count
    navigate('/checkout', { 
      state: { 
        userCount: company?.userLimit || 1, 
        companyId: company?.id 
      } 
    });
  };

  const handleManageBilling = async () => {
    // If user is on trial (no customerId), redirect to payment setup
    if (!company?.customerId) {
      handleSetupPayment();
      return;
    }

    setOpeningPortal(true);
    try {
      const functions = getFunctions();
      const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');
      
      const result = await createPortalSession({
        companyId: company.id,
        returnUrl: window.location.origin + '/subscription',
      });

      const { url } = result.data;
      window.location.href = url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
      alert(t('subscription.management.billingPortalError', 'Failed to open billing portal. Please try again.'));
      setOpeningPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm(t('subscription.management.cancelConfirm', 'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.'))) {
      return;
    }

    setCancelling(true);
    try {
      const functions = getFunctions();
      const cancelSubscription = httpsCallable(functions, 'cancelStripeSubscription');
      
      await cancelSubscription({
        companyId: company.id,
      });

      // Reload company data
      const companyDoc = await getDoc(doc(db, 'companies', company.id));
      const updatedCompany = { id: company.id, ...companyDoc.data() };
      setCompany(updatedCompany);

      alert(t('subscription.management.cancelSuccess', 'Subscription cancelled successfully. You will continue to have access until the end of your billing period.'));
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      alert(t('subscription.management.cancelError', 'Failed to cancel subscription. Please try again.'));
    } finally {
      setCancelling(false);
    }
  };

  const handleUpdateSubscription = async () => {
    setSaving(true);
    try {
      const isActive = company?.subscriptionStatus === 'active';
      
      if (isActive) {
        // For active subscriptions, use Stripe billing portal
        await handleManageBilling();
      } else {
        // For trial users, update userLimit directly in Firestore
        if (userCount === company?.userLimit) {
          alert(t('subscription.management.userCountSame', 'User count is already set to this value'));
          setSaving(false);
          return;
        }

        // Get current number of users in the company
        const usersQuery = query(
          collection(db, 'companies', company.id, 'users')
        );
        const usersSnapshot = await getDocs(usersQuery);
        const currentUserCount = usersSnapshot.size;

        // Check if new limit is less than current users
        if (userCount < currentUserCount) {
          alert(t('subscription.management.cannotReduceUsers', `Cannot reduce user limit to ${userCount}. You currently have ${currentUserCount} users. Please remove users first.`));
          setSaving(false);
          return;
        }

        // Update company userLimit
        await updateDoc(doc(db, 'companies', company.id), {
          userLimit: userCount
        });

        // Reload company data
        const companyDoc = await getDoc(doc(db, 'companies', company.id));
        const updatedCompany = { id: company.id, ...companyDoc.data() };
        setCompany(updatedCompany);

        alert(t('subscription.management.userLimitUpdated', `User limit updated to ${userCount}. This will be your subscription size when you activate payment.`));
      }
    } catch (err) {
      console.error('Failed to update subscription:', err);
      alert(err.message || t('subscription.management.updateError', 'Failed to update subscription'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async () => {
    const confirmText = `DELETE ${company?.name}`;
    const userInput = window.prompt(
      t('subscription.management.deleteWarning',
      `⚠️ DANGER ZONE ⚠️\n\n` +
      `This will permanently delete your company account and ALL associated data:\n` +
      `• All users will be deleted\n` +
      `• All forms will be deleted\n` +
      `• All submissions will be deleted\n` +
      `• All assets will be deleted\n` +
      `• Your subscription will be cancelled\n\n` +
      `This action CANNOT be undone!\n\n` +
      `To confirm, type: ${confirmText}`)
    );

    if (userInput !== confirmText) {
      if (userInput !== null) {
        alert(t('subscription.management.deletionCancelled', 'Deletion cancelled. Text did not match.'));
      }
      return;
    }

    setDeletingCompany(true);
    try {
      const functions = getFunctions();
      const deleteCompany = httpsCallable(functions, 'deleteCompany');
      
      await deleteCompany({
        companyId: company.id
      });

      alert(t('subscription.management.deleteSuccess', 'Company deleted successfully. You will be logged out.'));
      
      // Sign out and redirect to home
      await auth.signOut();
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to delete company:', err);
      alert(err.message || t('subscription.management.deleteError', 'Failed to delete company. Please try again.'));
      setDeletingCompany(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-50 relative overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -left-24 -top-32 w-80 h-80 bg-red-600/20 blur-3xl rounded-full" />
          <div className="absolute right-16 top-10 w-96 h-96 bg-rose-500/18 blur-3xl rounded-full" />
          <div className="absolute -right-32 bottom-16 w-72 h-72 bg-amber-400/12 blur-3xl rounded-full" />
        </div>
        <div className="relative z-10 flex flex-col items-center gap-4">
          <div className="h-12 w-12 border-2 border-rose-400/60 border-t-transparent rounded-full animate-spin" />
          <div className="text-gray-200 text-lg">{t('common.loading', 'Loading subscription...')}</div>
        </div>
      </div>
    );
  }

  const monthlyTotal = userCount * 5;
  const isActive = company?.subscriptionStatus === 'active';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-24 -top-32 w-80 h-80 bg-red-600/20 blur-3xl rounded-full" />
        <div className="absolute right-16 top-10 w-96 h-96 bg-rose-500/18 blur-3xl rounded-full" />
        <div className="absolute -right-32 bottom-16 w-72 h-72 bg-amber-400/12 blur-3xl rounded-full" />
      </div>

      <header className="relative z-10 border-b border-white/10 bg-white/5 backdrop-blur-xl shadow-lg">
        <div className="max-w-5xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{t('subscription.management.billing', 'Billing')}</p>
            <h1 className="text-3xl font-bold text-white">{t('subscription.management.title', 'Subscription management')}</h1>
            <p className="text-sm text-gray-300">{company?.name || t('subscription.management.company', 'Company')}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition"
            >
              ← {t('subscription.management.backToDashboard', 'Back to dashboard')}
            </button>
            <button
              onClick={() => navigate('/checkout')}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-rose-600 to-amber-500 hover:from-rose-500 hover:to-amber-400 text-white shadow-lg shadow-rose-900/30 transition"
            >
              💳 {t('subscription.management.goToCheckout', 'Go to checkout')}
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-10 space-y-8">
        <div className="grid md:grid-cols-2 gap-4">
          {[{ label: t('subscription.management.statusLabel', 'Status'), value: isActive ? t('subscription.management.active', '✓ Active') : t('subscription.management.trial', '⏱ Trial'), color: isActive ? 'text-emerald-200' : 'text-amber-200' },
            { label: t('subscription.management.monthlyCostLabel', 'Monthly cost'), value: `£${((company?.userLimit || 0) * 5).toFixed(2)}`, color: 'text-white' },
            { label: t('subscription.management.userLimitLabel', 'User limit'), value: `${company?.userLimit || 0} ${t('subscription.management.users', 'users')}`, color: 'text-sky-200' },
            { label: t('subscription.management.planLabel', 'Plan'), value: t('subscription.management.professional', 'Professional'), color: 'text-gray-100' }].map((item, idx) => (
            <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur-lg shadow-xl">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{item.label}</p>
              <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl backdrop-blur-lg shadow-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">{t('subscription.management.companyLogo', 'Company logo')}</h2>
            <span className="text-xs text-gray-400">{t('subscription.management.logoUsedInReports', 'Used on PDF reports')}</span>
          </div>
          <p className="text-sm text-gray-300 mb-4">
            {t('subscription.management.logoDescription', 'Upload your company logo to appear on all exported PDF reports. Max 2 MB, image files only.')}
          </p>
          <div className="flex items-center gap-6">
            {company?.logoURL ? (
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-lg border border-white/20 bg-white/5 flex items-center justify-center overflow-hidden">
                  <img src={company.logoURL} alt="Company Logo" className="max-w-full max-h-full object-contain" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition text-sm text-center">
                    {uploadingLogo ? t('common.uploading', 'Uploading...') : t('subscription.management.changeLogo', 'Change logo')}
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploadingLogo} />
                  </label>
                  <button
                    onClick={handleLogoRemove}
                    disabled={uploadingLogo}
                    className="px-4 py-2 rounded-lg bg-rose-500/20 border border-rose-400/30 text-rose-200 hover:bg-rose-500/30 transition text-sm disabled:opacity-50"
                  >
                    {t('subscription.management.removeLogo', 'Remove logo')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-lg border-2 border-dashed border-white/20 bg-white/5 flex items-center justify-center text-gray-500 text-3xl">
                  🏢
                </div>
                <label className="cursor-pointer px-5 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-rose-500 hover:from-emerald-400 hover:to-rose-400 text-white font-semibold shadow-lg shadow-rose-900/30 transition text-sm">
                  {uploadingLogo ? t('common.uploading', 'Uploading...') : t('subscription.management.uploadLogo', 'Upload company logo')}
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploadingLogo} />
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl backdrop-blur-lg shadow-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">{t('subscription.management.adjustUserCount', 'Adjust user count')}</h2>
            <span className="text-xs text-gray-400">{t('subscription.management.pricePerUser', '£5 per user / month')}</span>
          </div>
          {!isActive && (
            <div className="mb-4 p-3 bg-sky-500/10 border border-sky-400/30 rounded-lg text-sm text-sky-100">
              💡 {t('subscription.management.trialAdjustInfo', 'You can adjust your user count anytime during the trial. This will be your subscription size when you activate payment.')}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">{t('subscription.management.numberOfUsers', 'Number of users')}</label>
              <input
                type="number"
                min="1"
                max="100"
                value={userCount}
                onChange={(e) => setUserCount(parseInt(e.target.value) || 1)}
                className="w-full px-4 py-3 border border-white/10 rounded-lg bg-white/5 text-gray-50 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-500"
              />
              <p className="text-sm text-gray-400 mt-1">{t('subscription.management.seatsInfo', 'Enter your required seats for billing.')}</p>
            </div>

            <div className="bg-black/30 rounded-lg p-4 border border-white/10">
              <div className="flex justify-between items-center mb-2 text-sm text-gray-200">
                <span>Users</span>
                <span className="font-semibold text-white">{userCount}</span>
              </div>
              <div className="flex justify-between items-center mb-2 text-sm text-gray-200">
                <span>{t('subscription.management.pricePerUserLine', 'Price per user')}</span>
                <span className="font-semibold text-white">{t('subscription.management.fivePerMonth', '£5/month')}</span>
              </div>
              <div className="border-t border-white/10 pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-white">{isActive ? t('subscription.management.newMonthlyTotal', 'New monthly total') : t('subscription.management.expectedMonthlyTotal', 'Expected monthly total')}</span>
                  <span className="text-2xl font-bold text-emerald-200">£{monthlyTotal}</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleUpdateSubscription}
              disabled={saving || userCount === company?.userLimit}
              className="w-full px-5 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-rose-500 hover:from-emerald-400 hover:to-rose-400 text-white font-semibold shadow-lg shadow-rose-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('common.updating', 'Updating...') : isActive ? t('subscription.management.updateViaBilling', 'Update via billing portal') : t('subscription.management.updateUserLimit', 'Update user limit')}
            </button>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl backdrop-blur-lg shadow-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">{t('subscription.management.billingPayment', 'Billing & payment')}</h2>
            <span className="text-xs text-gray-400">{t('subscription.management.stripeManaged', 'Stripe-managed')}</span>
          </div>
          {isActive ? (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-200/30 text-sm text-emerald-100">
                <div className="flex items-center gap-2 font-semibold mb-2">
                  <span className="text-xl">✓</span>
                  <span>{t('subscription.management.activeSubscription', 'Active subscription')}</span>
                </div>
                <p>{t('subscription.management.manageInStripe', 'Manage billing, payment methods, and invoices in the Stripe customer portal.')}</p>
              </div>

              <button
                onClick={handleManageBilling}
                disabled={openingPortal}
                className="w-full px-5 py-3 rounded-xl bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition font-semibold disabled:opacity-50"
              >
                {openingPortal ? t('common.openingPortal', 'Opening portal...') : t('subscription.management.openBillingPortal', '🔗 Open billing portal')}
              </button>

              <div className="border-t border-white/10 pt-4 mt-4">
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="w-full px-5 py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold shadow-lg shadow-rose-900/30 disabled:opacity-50"
                >
                  {cancelling ? t('common.cancelling', 'Cancelling...') : t('subscription.management.cancelSubscription', '❌ Cancel subscription')}
                </button>
                <p className="text-xs text-gray-400 text-center mt-2">
                  {t('subscription.management.cancelsAtEnd', 'Cancels at the end of the current billing period.')}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-200/30 text-sm text-amber-100">
                <div className="flex items-center gap-2 font-semibold mb-2">
                  <span className="text-xl">⏱</span>
                  <span>{t('subscription.management.trialPeriod', 'Trial period')}</span>
                </div>
                <p>{t('subscription.management.trialSetupPayment', 'Set up your payment method to activate when the trial ends.')}</p>
                {company?.trialEndsAt && (
                  <p className="text-xs text-amber-200 mt-2">
                    {t('subscription.management.trialEnds', 'Trial ends:')} {new Date(company.trialEndsAt.toDate ? company.trialEndsAt.toDate() : company.trialEndsAt).toLocaleDateString()}
                  </p>
                )}
              </div>

              <button
                onClick={handleSetupPayment}
                className="w-full px-5 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-rose-500 hover:from-emerald-400 hover:to-rose-400 text-white font-semibold shadow-lg shadow-rose-900/30"
              >
                💳 {t('subscription.management.setupStripe', 'Set up payment with Stripe')}
              </button>

              <p className="text-xs text-gray-400 text-center">
                {t('subscription.management.autoActivate', 'Your subscription will activate automatically at trial end (or immediately after setup).')}
              </p>
            </div>
          )}
        </div>

        <div className="bg-white/5 border border-rose-300/30 rounded-2xl backdrop-blur-lg shadow-xl p-6">
          <h2 className="text-lg font-semibold text-rose-100 mb-3">⚠️ {t('subscription.management.dangerZone', 'Danger zone')}</h2>
          <div className="bg-rose-500/10 rounded-lg p-4 border border-rose-300/30 text-sm text-rose-100 mb-4">
            <p className="font-semibold mb-1">{t('subscription.management.deleteCompanyTitle', 'Delete company account')}</p>
            <p className="text-xs">{t('subscription.management.deleteCompanyDesc', 'This permanently deletes your company and all data (users, forms, submissions, assets). This cannot be undone.')}</p>
          </div>

          <button
            onClick={handleDeleteCompany}
            disabled={deletingCompany}
            className="w-full px-5 py-3 rounded-xl bg-rose-700 hover:bg-rose-800 text-white font-semibold shadow-lg shadow-rose-900/40 border border-rose-400/40 disabled:opacity-50"
          >
            {deletingCompany ? t('common.deletingCompany', 'Deleting company...') : t('subscription.management.deleteCompanyBtn', '🗑️ Delete company account')}
          </button>
        </div>
      </main>
    </div>
  );
}

// ----------------------------------------------------------------------------------------------
//  Build My Checks (c) 2025 Gareth Morgan - BuildMyChecks.co.uk
//  Solely designed, developed, and maintained by Gareth Morgan.
//  All intellectual property rights reserved.
//  No liability is accepted for unauthorised use, modification, deployment, or maintenance.
// ----------------------------------------------------------------------------------------------
